/* eslint max-lines: ["error", { "max": 655 }] */
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CreateEmployeeDto,
  EmploymentType,
  Gender,
  GetEmployerDto,
  OpenEnrollmentPeriodDto,
  PossibleFipsCode,
  WageType,
} from '@zorro/clients';
import {
  SUCCESS_MESSAGES,
  VALIDATION_MESSAGES,
  getRequiredClassCategoriesForTargetYear,
} from '@zorro/common-utils';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Select } from '@zorro/design-system/v2/Select';
import { SsnPasswordInput } from '@zorro/design-system/v2/SsnPasswordInput';
import { TabButtonGroup } from '@zorro/design-system/v2/TabButtonGroup';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import {
  formatDateISO,
  getDateUtil,
  getNowAsDate,
  parseDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import { SubEntityVariant } from '@zorro/types';
import {
  AddressInput,
  CurrencyInput,
  DateInput,
  FormErrorMessage,
  Grid,
  InputWrapper,
  LabelWithTooltip,
  MonthPickerInput,
  PhoneInput,
  Space,
  Stack,
} from '@zorro/web/design-system';
import {
  extractCustomClassCategories,
  getAdultDefaultDate,
  useForm,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
  validateAddress,
} from '@zorro/web/ui-utils';
import { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';

import { EmployeeSubEntityInput } from '../EmployeeSubEntityInput/EmployeeSubEntityInput';
import { FormFooter } from '../FormFooter';
import { FullPageLoader } from '../FullPageLoader';
import {
  CreateEmployeeFormFields,
  addEmployeeSchema,
  calculateEligibleFrom,
} from './CreateEmployeeFormUtils';
import { useCompanyEmailAvailability } from './useCompanyEmailAvailability';

export type CreateEmployeeFormProps = {
  employerId: GetEmployerDto['id'];
  employerWaitingPeriod: GetEmployerDto['waitingPeriod'];
  enrollmentPeriods: OpenEnrollmentPeriodDto[];
  onSuccess: () => void;
  onDirtyChange: (isDirty: boolean) => void;
};

export const CreateEmployeeForm = ({
  employerId,
  employerWaitingPeriod,
  enrollmentPeriods,
  onSuccess,
  onDirtyChange,
}: CreateEmployeeFormProps) => {
  'use no memo';
  const { tryMutate: createEmployee } = useMonolithMutation({
    method: 'employeesControllerCreate',
    successMessage: SUCCESS_MESSAGES.EMPLOYEE_ADD_SUCCESS_MESSAGE,
  });

  const { data: subEntities = [] } = useMonolithQuery({
    method: 'subEntityControllerFindMany',
    params: employerId && [employerId],
  });

  const { data: subEntities2 = [] } = useMonolithQuery({
    method: 'subEntity2ControllerFindMany',
    params: employerId && [employerId],
  });

  const isSubEntity2Enabled = subEntities2.length > 0;

  const {
    data: { classDefinitions } = { classDefinitions: [] },
    isLoading: isLoadingClassDefinitions,
  } = useMonolithQuery({
    method: 'allowanceModelControllerGetAllClassDefinitionsForEmployer',
    params: employerId && [employerId],
  });

  const now = getNowAsDate();
  const currentYear = now.getFullYear();

  const uniqueCustomClassCategoryNames = extractCustomClassCategories(
    classDefinitions,
    currentYear,
    true
  );

  const requiredClassCategories = getRequiredClassCategoriesForTargetYear(
    classDefinitions,
    [currentYear, currentYear + 1]
  );

  const showCustomClassCategory =
    requiredClassCategories.hasCustomClassCategory;

  const minEffectiveFrom =
    getDateUtil().min(
      enrollmentPeriods.map((item) => {
        return parseDateISO(item.effectiveFrom);
      })
    ) || '';

  const effectiveFrom = formatDateISO(minEffectiveFrom);

  const { startLoading, stopLoading } = useLoadingOverlay();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [possibleFipsCodes, setPossibleFipsCodes] = useState<
    PossibleFipsCode[]
  >([]);

  const form = useForm<CreateEmployeeFormFields>({
    mode: 'all',
    resolver: yupResolver(
      addEmployeeSchema(employerWaitingPeriod ?? null, requiredClassCategories)
    ),
    defaultValues: {
      sendActivationEmail: true,
    },
  });

  const { control, formState, watch, setValue, getValues, setError } = form;
  const { errors, isValid, dirtyFields } = formState;
  const { checkEmailOnBlur, isEmailInUse, emailInUseMessage } =
    useCompanyEmailAvailability(form);

  const hireDate = watch('hireDate');

  useEffect(() => {
    const calculatedEligibleFrom = getDateUtil().max(
      calculateEligibleFrom(hireDate, employerWaitingPeriod),
      parseDateISO(effectiveFrom)
    );

    const eligibleFrom = getValues('eligibleFrom');
    const parsedEligibleFrom = eligibleFrom && parseDate(eligibleFrom);

    if (hireDate && !calculatedEligibleFrom.isSame(parsedEligibleFrom, 'day')) {
      setValue('eligibleFrom', calculatedEligibleFrom.toDate(), {
        shouldValidate: true,
      });
    }
  }, [getValues, setValue, hireDate, employerWaitingPeriod, effectiveFrom]);

  // isDirty compares the whole form, so any change event flips it once fields register
  const hasChangedFields = Object.keys(dirtyFields).length > 0;
  useEffect(() => {
    onDirtyChange(hasChangedFields);
  }, [hasChangedFields, onDirtyChange]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const { residentialAddress, ...data } = getValues();

    startLoading();

    const employee: CreateEmployeeDto = {
      ...data,
      dateOfBirth: formatDateISO(data.dateOfBirth),
      eligibleFrom: data.eligibleFrom ? formatDateISO(data.eligibleFrom) : null,
      hireDate: data.hireDate ? formatDateISO(data.hireDate) : null,
      sendActivationEmail: data.sendActivationEmail,
      employerId,
      address: residentialAddress,
      personalEmail: data.personalEmail || null,
      salary: data.salary || null,
      subEntityId: data.subEntityId || null,
      subEntity2Id: data.subEntity2Id || null,
      customClassCategory: data.customClassCategory || null,
    };

    if (residentialAddress) {
      const { isValidAddress, possibleFipsCodes } =
        await validateAddress(residentialAddress);
      if (!isValidAddress) {
        setError('residentialAddress', {
          message: VALIDATION_MESSAGES.residentialAddressInvalid,
        });
        stopLoading();
        setIsSubmitting(false);
        return;
      } else if (
        possibleFipsCodes &&
        possibleFipsCodes.length > 1 &&
        !data.fipsCode
      ) {
        setPossibleFipsCodes(possibleFipsCodes);
        setError('fipsCode', {
          message: VALIDATION_MESSAGES.countyDisambiguity,
        });
        stopLoading();
        setIsSubmitting(false);
        return;
      }
    }

    const result = await createEmployee([employee]);
    if (result.isErr()) {
      stopLoading();
      setIsSubmitting(false);
      return;
    }
    onSuccess();

    setIsSubmitting(false);
  };

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Add employee"
        primaryButtonProps={{
          disabled: !isValid || isSubmitting || isEmailInUse,
          onClick: handleSubmit,
          // @ts-expect-error - data-testid not in type but needed for testing
          'data-testid': 'submit-button',
        }}
      />
    ),
    [handleSubmit, isValid, isSubmitting, isEmailInUse]
  );
  useDrawerFooter(drawerFooter);

  if (isLoadingClassDefinitions) {
    return <FullPageLoader />;
  }
  return (
    <Stack mih="calc(100vh - 160px)" px="xxl" pb="xxl">
      <Grid>
        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="firstName"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                required
                label="First name"
                placeholder="First name"
                data-testid="first-name-input"
              />
            )}
          />

          <FormErrorMessage fieldName="firstName" errors={errors} />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="lastName"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                required
                label="Last name"
                placeholder="Last name"
                data-testid="last-name-input"
              />
            )}
          />

          <FormErrorMessage fieldName="lastName" errors={errors} />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="email"
            render={({ field: { onBlur, ...rest } }) => (
              <TextInput
                {...rest}
                onBlur={() => {
                  onBlur();
                  checkEmailOnBlur();
                }}
                required
                label="Company email"
                placeholder="Company email"
                data-testid="company-email-input"
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="email" />
          {emailInUseMessage}
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="personalEmail"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                label="Personal email"
                placeholder="Personal email"
                data-testid="personal-email-input"
              />
            )}
          />

          <FormErrorMessage fieldName="personalEmail" errors={errors} />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                {...field}
                label="Phone number"
                placeholder="Phone number"
                data-testid="phone-input"
              />
            )}
          />

          <FormErrorMessage fieldName="phone" errors={errors} />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="idFromEmployer"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                label="Employee ID"
                placeholder="Employee ID"
                data-testid="employee-id-input"
              />
            )}
          />

          <FormErrorMessage fieldName="idFromEmployer" errors={errors} />
        </Grid.Col>
        {subEntities.length > 0 && (
          <Grid.Col span={{ sm: 6 }}>
            <EmployeeSubEntityInput
              control={control}
              name="subEntityId"
              employerId={employerId}
              type={SubEntityVariant.subEntity}
              selectProps={{
                label: 'Sub-entity 1',
              }}
              data-testid="sub-entity-id-select"
            />
          </Grid.Col>
        )}
        {isSubEntity2Enabled && (
          <Grid.Col span={{ sm: 6 }}>
            <EmployeeSubEntityInput
              control={control}
              name="subEntity2Id"
              employerId={employerId}
              type={SubEntityVariant.subEntity2}
              data-testid="sub-entity-2-id-select"
            />
          </Grid.Col>
        )}

        <Grid.Col>
          <Controller
            control={control}
            name="residentialAddress"
            render={({ field: { onChange, onBlur, value, ...rest } }) => (
              <AddressInput
                {...rest}
                value={value}
                onBlur={() => {
                  onChange(value);
                  onBlur();
                  setPossibleFipsCodes([]);
                  setValue('fipsCode', undefined);
                }}
                notifySubscribers={(address: string) => onChange(address)}
                onChange={onChange}
                label="Address"
                placeholder="Address"
                required
                data-testid="residential-address-input"
              />
            )}
          />
          <FormErrorMessage errors={errors} fieldName="residentialAddress" />
        </Grid.Col>
        {possibleFipsCodes.length > 1 && (
          <Grid.Col>
            <Controller
              control={control}
              name="fipsCode"
              render={({ field: { ...rest } }) => (
                <Select
                  {...rest}
                  label="County of residence"
                  placeholder="Select county of residence"
                  data={possibleFipsCodes.map(({ fipsCode, county }) => ({
                    label: county,
                    value: fipsCode,
                  }))}
                  data-testid="fips-code-select"
                />
              )}
            />
            <FormErrorMessage errors={errors} fieldName="fipsCode" />
          </Grid.Col>
        )}

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="dateOfBirth"
            render={({ field: { ...rest } }) => (
              <DateInput
                {...rest}
                required
                label="Date of birth"
                defaultLevel="decade"
                defaultDate={getAdultDefaultDate()}
                data-testid="date-of-birth-input"
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="dateOfBirth" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="gender"
            render={({ field: { onChange, ...rest } }) => (
              <InputWrapper label="Gender">
                <TabButtonGroup
                  {...rest}
                  onChange={onChange}
                  data-testid="gender-tabgroup"
                  size="md"
                  isFullWidth
                >
                  <TabButtonGroup.List grow>
                    <TabButtonGroup.Tab
                      value={Gender.MALE}
                      data-testid="gender-male-tab"
                    >
                      Male
                    </TabButtonGroup.Tab>
                    <TabButtonGroup.Tab
                      value={Gender.FEMALE}
                      data-testid="gender-female-tab"
                    >
                      Female
                    </TabButtonGroup.Tab>
                  </TabButtonGroup.List>
                </TabButtonGroup>
              </InputWrapper>
            )}
          />

          <FormErrorMessage errors={errors} fieldName="gender" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="salary"
            render={({ field: { ...rest } }) => (
              <CurrencyInput
                {...rest}
                label="Yearly salary"
                placeholder="Yearly salary"
                data-testid="salary-input"
              />
            )}
          />

          <FormErrorMessage fieldName="salary" errors={errors} />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="hireDate"
            render={({ field: { ...rest } }) => (
              <DateInput
                {...rest}
                required
                label="Hire date"
                data-testid="hire-date-input"
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="hireDate" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="eligibleFrom"
            render={({ field: { ...rest } }) => (
              <MonthPickerInput
                {...rest}
                label="Eligibility start date"
                monthsListFormat="MMM D"
                valueFormat="MMMM D, YYYY"
                minDate={
                  effectiveFrom
                    ? parseDateISO(effectiveFrom).toDate()
                    : undefined
                }
                data-testid="eligible-from-input"
              />
            )}
          />
          <FormErrorMessage errors={errors} fieldName="eligibleFrom" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="ssn"
            render={({ field: { value, ...rest } }) => (
              <SsnPasswordInput
                {...rest}
                label="SSN"
                value={value || ''}
                data-testid="ssn-input"
              />
            )}
          />
          <FormErrorMessage fieldName="ssn" errors={errors} />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="employmentType"
            render={({ field: { ...rest } }) => (
              <Select
                {...rest}
                data-testid="employment-type-select"
                label="Employment type"
                placeholder="Select employment type"
                data={[
                  { value: EmploymentType.FULL_TIME, label: 'Full Time' },
                  { value: EmploymentType.PART_TIME, label: 'Part Time' },
                ]}
                required={requiredClassCategories.hasEmploymentType}
              />
            )}
          />
          <FormErrorMessage fieldName="employmentType" errors={errors} />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="wageType"
            render={({ field: { ...rest } }) => (
              <Select
                {...rest}
                label="Wage type"
                placeholder="Select wage type"
                data={[
                  { value: WageType.SALARY, label: 'Salary' },
                  { value: WageType.HOURLY, label: 'Hourly' },
                ]}
                required={requiredClassCategories.hasWageType}
                data-testid="wage-type-select"
              />
            )}
          />
          <FormErrorMessage fieldName="wageType" errors={errors} />
        </Grid.Col>

        {showCustomClassCategory && (
          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="customClassCategory"
              render={({ field: { ...rest } }) => (
                <Select
                  {...rest}
                  label="Custom class category"
                  placeholder="Select a custom class category"
                  data={uniqueCustomClassCategoryNames.map((className) => ({
                    value: className,
                    label: className,
                  }))}
                  required
                  data-testid="custom-class-category-select"
                />
              )}
            />
            <FormErrorMessage fieldName="customClassCategory" errors={errors} />
          </Grid.Col>
        )}
      </Grid>

      <Space h="lg" />

      <Controller
        control={control}
        name="sendActivationEmail"
        render={({ field: { value, ...rest } }) => (
          <Checkbox
            {...rest}
            checked={value}
            label={
              <LabelWithTooltip
                label="Send invitation"
                tooltipLabel="Employees need an invitation to set up their Zorro account and log in for the first time"
              />
            }
            data-testid="send-activation-email-checkbox"
          />
        )}
      />
    </Stack>
  );
};
